import { colors } from '../styles/utils/colors';
import { spacing } from '../styles/utils/spacing';
import { typography } from '../styles/utils/typography';

/**
 * API 에러를 처리하고 사용자에게 표시할 메시지를 반환
 * @param {Error} error - axios 에러 객체
 * @param {string} customMessage - 커스텀 에러 메시지 (선택)
 * @param {Function} onError - 추가 에러 처리 콜백 (선택)
 * @returns {string} 사용자에게 표시할 에러 메시지
 */
export const handleApiError = (error, customMessage = null, onError = null) => {
  let message = customMessage;

  if (!message) {
    if (error.response) {
      // 서버가 응답했지만 에러 상태 코드
      message =
        error.response.data?.RESULT_MESSAGE ||
        error.response.data?.message ||
        `서버 오류: ${error.response.status}`;
      
      if (process.env.NODE_ENV === "development") {
        console.error("API Response Error:", {
          status: error.response.status,
          data: error.response.data,
          error,
        });
      }
    } else if (error.request) {
      // 요청은 보냈지만 응답을 받지 못함
      message = "서버에 연결할 수 없습니다. 네트워크 연결을 확인하세요.";
      
      if (process.env.NODE_ENV === "development") {
        console.error("API Request Error (No Response):", error.request);
      }
    } else {
      // 요청 설정 중 오류
      message = `요청 오류: ${error.message || "알 수 없는 오류가 발생했습니다."}`;
      
      if (process.env.NODE_ENV === "development") {
        console.error("API Setup Error:", error.message);
      }
    }
  }

  // 추가 에러 처리 콜백 실행
  if (onError && typeof onError === "function") {
    onError(error, message);
  }

  return message;
};

/**
 * 에러 메시지를 사용자에게 알림 (화면 중앙에 표시)
 * @param {string} message - 표시할 메시지
 * @param {string} type - 메시지 타입 ('error' | 'warning' | 'info')
 */
export const showError = (message, type = "error") => {
  // 기존 모달이 있으면 제거
  const existingModal = document.getElementById("custom-alert-modal");
  if (existingModal) {
    existingModal.remove();
  }

  // 타입별 색상 및 아이콘 설정 (styles의 colors 사용)
  const typeConfig = {
    error: {
      iconColor: colors.error,
      buttonColor: colors.error,
      iconPath: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
    },
    warning: {
      iconColor: colors.warning,
      buttonColor: colors.warning,
      iconPath: "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
    },
    info: {
      iconColor: colors.primary,
      buttonColor: colors.primary,
      iconPath: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
    }
  };

  const config = typeConfig[type] || typeConfig.error;

  // 모달 오버레이 생성
  const overlay = document.createElement("div");
  overlay.id = "custom-alert-modal";
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    animation: fadein 0.2s;
  `;

  // 모달 박스 생성
  const modalBox = document.createElement("div");
  modalBox.style.cssText = `
    background-color: ${colors.backgroundPaper};
    border-radius: ${spacing.md};
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    min-width: 300px;
    max-width: 500px;
    padding: ${spacing.xxl};
    text-align: center;
    animation: slidein 0.2s;
  `;

  // 아이콘 컨테이너 생성
  const iconContainer = document.createElement("div");
  iconContainer.style.cssText = `
    margin-bottom: ${spacing.lg};
    display: flex;
    justify-content: center;
    align-items: center;
  `;

  const iconSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  iconSvg.setAttribute("width", "48");
  iconSvg.setAttribute("height", "48");
  iconSvg.setAttribute("viewBox", "0 0 24 24");
  iconSvg.style.cssText = `
    fill: ${config.iconColor};
  `;

  const iconPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
  iconPath.setAttribute("d", config.iconPath);
  iconSvg.appendChild(iconPath);
  iconContainer.appendChild(iconSvg);

  // 메시지 텍스트 (줄바꿈 처리)
  const messageText = document.createElement("div");
  messageText.style.cssText = `
    font-size: ${typography.fontSize.md};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.textPrimary};
    margin-bottom: ${spacing.xxl};
    word-wrap: break-word;
    white-space: pre-wrap;
    text-align: center;
  `;
  // 접두사 없이 원본 메시지만 표시하고 줄바꿈 처리
  messageText.textContent = message;

  // 확인 버튼
  const confirmButton = document.createElement("button");
  confirmButton.textContent = "확인";
  confirmButton.style.cssText = `
    background-color: ${config.buttonColor};
    color: ${colors.white};
    border: none;
    border-radius: ${spacing.sm};
    padding: 10px ${spacing.xxl};
    font-size: ${typography.fontSize.md};
    font-weight: ${typography.fontWeight.medium};
    cursor: pointer;
    min-width: 80px;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  `;
  confirmButton.onmouseover = () => {
    confirmButton.style.opacity = "0.9";
    confirmButton.style.transform = "translateY(-1px)";
    confirmButton.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.3)";
  };
  confirmButton.onmouseout = () => {
    confirmButton.style.opacity = "1";
    confirmButton.style.transform = "translateY(0)";
    confirmButton.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.2)";
  };
  confirmButton.onclick = () => {
    overlay.remove();
  };

  // 키보드 이벤트 (Enter, Escape)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Escape") {
      overlay.remove();
      document.removeEventListener("keydown", handleKeyDown);
    }
  };
  document.addEventListener("keydown", handleKeyDown);

  // 모달 조립
  modalBox.appendChild(iconContainer);
  modalBox.appendChild(messageText);
  modalBox.appendChild(confirmButton);
  overlay.appendChild(modalBox);
  document.body.appendChild(overlay);

  // 오버레이 클릭 시 닫기
  overlay.onclick = (e) => {
    if (e.target === overlay) {
      overlay.remove();
      document.removeEventListener("keydown", handleKeyDown);
    }
  };

  // 포커스 설정
  confirmButton.focus();
};

/**
 * API 에러를 처리하고 사용자에게 알림
 * @param {Error} error - axios 에러 객체
 * @param {string} customMessage - 커스텀 에러 메시지 (선택)
 * @param {Function} onError - 추가 에러 처리 콜백 (선택)
 */
export const handleAndShowError = (error, customMessage = null, onError = null) => {
  const message = handleApiError(error, customMessage, onError);
  showError(message);
  return message;
};

/**
 * 조회 결과가 없을 때의 메시지 처리
 * @param {Object} response - API 응답 객체
 * @param {string} defaultMessage - 기본 메시지
 * @returns {boolean} 데이터가 있는지 여부
 */
export const checkDataExists = (response, defaultMessage = "조회된 내역이 없습니다.") => {
  if (response?.code === "0" && response?.data && response.data.length > 0) {
    return true;
  }
  
  const message = response?.message || response?.RESULT_MESSAGE || defaultMessage;
  
  // "조회된 데이터가 없습니다"는 오류가 아닌 정보성 메시지로 처리
  const isNoDataMessage = message.includes("조회된 데이터가 없습니다") || 
                          message.includes("조회된 내역이 없습니다");
  
  if (response?.code !== "0") {
    // 데이터 없음 메시지는 info 타입으로, 그 외는 error 타입으로 표시
    showError(message, isNoDataMessage ? "info" : "error");
  } else {
    showError(message, "info");
  }
  
  return false;
};

const errorHandler = {
  handleApiError,
  showError,
  handleAndShowError,
  checkDataExists,
};

export default errorHandler;
